import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const VARIAVEIS_FIXAS = [
  { k: "nome", d: "nome do cliente" },
  { k: "numero", d: "número do cliente" },
  { k: "primeiro_nome", d: "primeiro nome" },
  { k: "anuncio_id", d: "ID do anúncio (Click-to-WhatsApp)" },
  { k: "anuncio_titulo", d: "título do anúncio" },
];

// Lê as variáveis criadas no próprio fluxo (bloco Variável, Aguarda Resposta, Pergunta)
const variaveisDoFluxo = (nodes) => {
  const achadas = [];
  (Array.isArray(nodes) ? nodes : []).forEach((n) => {
    const d = n?.data || {};
    const nome =
      d?.variableConfig?.variableName ||
      d?.answerKey ||
      d?.data?.answerKey ||
      d?.typebotIntegration?.answerKey;
    if (nome && !achadas.includes(nome)) achadas.push(nome);
  });
  return achadas;
};

const FlowBuilderMetaPixelModal = ({ open, onSave, onUpdate, data, close, flowNodes }) => {
  const [activeModal, setActiveModal] = useState(false);
  const [pixels, setPixels] = useState([]);
  const [pixelKey, setPixelKey] = useState("");
  const [eventName, setEventName] = useState("Lead");
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState("BRL");
  const [pageId, setPageId] = useState("");
  const [foco, setFoco] = useState("value");

  const load = useCallback(async () => {
    try {
      const { data: list } = await api.get("/meta-pixels");
      setPixels(Array.isArray(list) ? list : []);
    } catch (err) {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    if (open === "edit") {
      const d = data?.data || data || {};
      setPixelKey(d.pixelKey || "");
      setEventName(d.eventName || "Lead");
      setValue(d.value || "");
      setCurrency(d.currency || "BRL");
      setPageId(d.pageId || "");
      setActiveModal(true);
      load();
    } else if (open === "create") {
      setPixelKey("");
      setEventName("Lead");
      setValue("");
      setCurrency("BRL");
      setPageId("");
      setActiveModal(true);
      load();
    }
  }, [open, data, load]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const inserirVariavel = (v) => {
    if (foco === "pageId") setPageId((old) => `${old}${v}`);
    else setValue((old) => `${old}${v}`);
  };

  const handleSave = () => {
    if (!pixelKey) {
      toast.error("Selecione um pixel configurado.");
      return;
    }
    if (eventName === "Purchase" && !String(value).trim()) {
      toast.error("Informe o valor da compra.");
      return;
    }
    const payload = { pixelKey, eventName, value, currency, pageId };
    if (open === "edit") {
      onUpdate({ ...data, data: { ...(data?.data || {}), ...payload } });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  const dica = {
    fontSize: 11,
    color: "#6b7280",
    display: "block",
    marginTop: 4,
    lineHeight: 1.5,
  };

  return (
    <Dialog open={activeModal} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {open === "create" ? "Adicionar Pixel da Meta" : "Editar Pixel"}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 14,
          }}
        >
          <TextField
            select
            label="Pixel configurado *"
            value={pixelKey}
            onChange={(e) => setPixelKey(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          >
            {pixels.length === 0 && (
              <MenuItem value="" disabled>
                Nenhum pixel cadastrado
              </MenuItem>
            )}
            {pixels.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name} ({p.pixelId})
              </MenuItem>
            ))}
          </TextField>
          <Typography style={dica}>
            Cadastre seus pixels no menu <b>Pixels do Facebook</b>.
          </Typography>

          <TextField
            select
            label="Tipo do evento *"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginTop: 14 }}
          >
            <MenuItem value="Lead">Lead (contato / interesse)</MenuItem>
            <MenuItem value="Purchase">Compra</MenuItem>
            <MenuItem value="Schedule">Agendamento</MenuItem>
            <MenuItem value="Contact">Contato</MenuItem>
            <MenuItem value="CompleteRegistration">Cadastro completo</MenuItem>
          </TextField>
          <Typography style={dica}>
            <b>Lead</b>: o cliente demonstrou interesse. <b>Compra</b>: use quando a venda
            for confirmada — nesse caso o valor é obrigatório.
          </Typography>

          <TextField
            label="Page ID"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
            onFocus={() => setFoco("pageId")}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Ex: 123456789012345 ou {{pagina_id}}"
            style={{ marginTop: 14 }}
          />
          <Typography style={dica}>
            ID da página do Facebook ligada ao WhatsApp Business. A Meta exige para
            eventos vindos do WhatsApp. Se você já cadastrou no pixel, pode deixar
            vazio — este campo apenas sobrescreve.
          </Typography>

          <TextField
            label="Valor do item"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFoco("value")}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Ex: 197,00 ou {{preco}}"
            style={{ marginTop: 14 }}
          />
          <Typography style={dica}>
            Obrigatório para o evento de <b>Compra</b>. Aceita variáveis do fluxo — clique
            nos botões abaixo para inserir.
          </Typography>

          <Box style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {VARIAVEIS_FIXAS.map((v) => (
              <Button
                key={v.k}
                size="small"
                variant="outlined"
                onClick={() => inserirVariavel(`{{${v.k}}}`)}
                style={{ textTransform: "none", fontSize: 11 }}
                title={`Insere ${v.d}`}
              >
                {`{{${v.k}}}`}
              </Button>
            ))}
            {variaveisDoFluxo(flowNodes).map((v) => (
              <Button
                key={`flow-${v}`}
                size="small"
                variant="outlined"
                onClick={() => inserirVariavel(`{{${v}}}`)}
                style={{
                  textTransform: "none",
                  fontSize: 11,
                  borderColor: "#a5b4fc",
                  color: "#4f46e5",
                }}
                title="Variável criada no seu fluxo"
              >
                {`{{${v}}}`}
              </Button>
            ))}
          </Box>
          <Typography style={dica}>
            As variáveis entram no campo que você clicou por último
            ({foco === "pageId" ? "Page ID" : "Valor do item"}). As
            <b style={{ color: "#4f46e5" }}> roxas</b> são as que você criou neste fluxo.
            Se a sua variável não estiver aqui, pode digitar à mão — funciona igual.
          </Typography>

          <TextField
            select
            label="Moeda *"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginTop: 14 }}
          >
            <MenuItem value="BRL">BRL — Real brasileiro</MenuItem>
            <MenuItem value="USD">USD — Dólar americano</MenuItem>
            <MenuItem value="EUR">EUR — Euro</MenuItem>
          </TextField>
          <Typography style={dica}>
            Enviada junto com o valor da compra. Padrão: BRL.
          </Typography>
        </Box>

        <Box
          style={{
            background: "#f8fafc",
            borderRadius: 10,
            padding: 12,
          }}
        >
          <Typography style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Como funciona
          </Typography>
          <Typography style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
            Quando o fluxo passa por este bloco, um evento é enviado ao Facebook pela
            API de Conversões. Se o cliente chegou por um anúncio Click-to-WhatsApp, o
            código de rastreio é enviado junto automaticamente — é assim que a Meta sabe
            qual anúncio gerou o resultado e otimiza a entrega. O telefone do cliente vai
            criptografado, como a Meta exige.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderMetaPixelModal;
